import type { PhotoLocation } from './types'

export interface PhotoLocator {
  /** Start a fix now so it is ready by the time the shutter fires. */
  prime: () => void
  /** The fresh fix, or null when the device declines or cannot say. */
  current: () => Promise<PhotoLocation | null>
}

const FIX_MAX_AGE_MS = 120_000
const FIX_TIMEOUT_MS = 10_000

export function createPhotoLocator(
  geolocation: Geolocation | undefined,
  now: () => number = Date.now
): PhotoLocator {
  let inFlight: Promise<PhotoLocation | null> | null = null
  let fixedAt = 0

  const fresh = () => inFlight !== null && now() - fixedAt < FIX_MAX_AGE_MS

  const prime = () => {
    if (!geolocation || fresh()) return
    fixedAt = now()
    inFlight = new Promise((resolve) => {
      geolocation.getCurrentPosition(
        ({ coords, timestamp }) =>
          resolve({
            lat: coords.latitude,
            lon: coords.longitude,
            accuracy_m: Number.isFinite(coords.accuracy)
              ? coords.accuracy
              : null,
            fixed_at: new Date(timestamp).toISOString(),
          }),
        () => resolve(null),
        {
          enableHighAccuracy: true,
          timeout: FIX_TIMEOUT_MS,
          maximumAge: FIX_MAX_AGE_MS,
        }
      )
    })
  }

  return {
    prime,
    current: () => {
      prime()
      return inFlight ?? Promise.resolve(null)
    },
  }
}

export const photoLocator = createPhotoLocator(
  typeof navigator === 'undefined' ? undefined : navigator.geolocation
)
